import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  getPeerReviewNominationsAPI, 
  createPeerReviewNominationsAPI, 
  deletePeerReviewNominationAPI,
  getManagerCycleDetailAPI,
  fetchAllEmployeesAPI
} from "api/apiHelper";
import Card from "components/Card";
import Button from "components/Button";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import { toast } from "react-toastify";
import { MdArrowBack, MdDelete, MdAdd, MdPeople } from "react-icons/md";

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email?: string;
  designation?: string;
}

interface PeerNomination {
  id: number;
  cycle: number;
  reviewer: Employee;
  reviewee: Employee;
  nominated_by: Employee;
  reviewer_name: string;
  reviewee_name: string;
  nominated_by_name: string;
}

const PeerNominations = () => {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cycle, setCycle] = useState<any>(null);
  const [nominations, setNominations] = useState<PeerNomination[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedReviewer, setSelectedReviewer] = useState<number | null>(null);
  const [selectedReviewee, setSelectedReviewee] = useState<number | null>(null);

  useEffect(() => {
    if (cycleId) {
      fetchData();
    }
  }, [cycleId]);

  useEffect(() => {
    if (cycle) {
      fetchEmployees();
    }
  }, [cycle]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [cycleResponse, nominationsResponse] = await Promise.all([
        getManagerCycleDetailAPI(Number(cycleId)),
        getPeerReviewNominationsAPI(Number(cycleId))
      ]);
      
      setCycle(cycleResponse.data);
      setNominations(nominationsResponse.data);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err.message || "Failed to load data.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      if (!cycle || !cycle.participants) {
        return;
      }
      
      const participantIds = cycle.participants.map((p: any) => p.id);
      const response = await fetchAllEmployeesAPI('', true, 1000);
      const employeesData = Array.isArray(response.data) 
        ? response.data 
        : response.data?.results || [];
      
      const filteredEmployees = employeesData.filter((emp: any) => {
        return emp.user_role === 'EMPLOYEE' && participantIds.includes(emp.id);
      });
      
      setEmployees(filteredEmployees);
    } catch (err: any) {
      toast.error("Failed to fetch employees");
    }
  };

  const handleAddNomination = async () => {
    if (!selectedReviewer || !selectedReviewee) {
      toast.error("Please select both reviewer and reviewee");
      return;
    }

    if (selectedReviewer === selectedReviewee) {
      toast.error("Reviewer and reviewee cannot be the same person");
      return;
    }

    try {
      setSubmitting(true);
      await createPeerReviewNominationsAPI(Number(cycleId), [
        { reviewer_id: selectedReviewer, reviewee_id: selectedReviewee }
      ]);
      toast.success("Peer nomination added successfully");
      setSelectedReviewer(null);
      setSelectedReviewee(null);
      setShowAddForm(false);
      fetchData();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to add peer nomination");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNomination = async (nominationId: number) => {
    if (!window.confirm("Are you sure you want to delete this peer nomination?")) {
      return;
    }

    try {
      setSubmitting(true);
      await deletePeerReviewNominationAPI(Number(cycleId), nominationId);
      toast.success("Peer nomination deleted successfully");
      fetchData();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to delete peer nomination");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Backdrop show={true} />;
  }

  return (
    <div className="mt-5">
      <div className="mb-6 flex items-center justify-between">
        <Button
          varient="secondary"
          onClick={() => navigate('/manager-appraisals')}
          className="flex items-center gap-2 px-4 py-2"
        >
          <MdArrowBack className="h-5 w-5" />
          Back
        </Button>
      </div>

      {error && <ErrorBox errorMessage={error} />}

      <Card extra="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white mb-2">
            Peer Review Nominations
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {cycle?.name}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage peer review assignments for this appraisal cycle. Employees can only review peers they are nominated for.
          </p>
        </div>

        <div className="mb-6">
          <Button
            varient="primary"
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2"
          >
            <MdAdd className="h-5 w-5" />
            Add Peer Nomination
          </Button>
        </div>

        {showAddForm && (
          <Card extra="p-6 mb-6 bg-gray-50 dark:bg-navy-800">
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Add New Peer Nomination
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                  Reviewer (Who will review)
                </label>
                <select
                  value={selectedReviewer || ''}
                  onChange={(e) => setSelectedReviewer(Number(e.target.value) || null)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white"
                >
                  <option value="">Select Reviewer</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name || `${emp.first_name} ${emp.last_name}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                  Reviewee (Who will be reviewed)
                </label>
                <select
                  value={selectedReviewee || ''}
                  onChange={(e) => setSelectedReviewee(Number(e.target.value) || null)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white"
                >
                  <option value="">Select Reviewee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name || `${emp.first_name} ${emp.last_name}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                varient="primary"
                onClick={handleAddNomination}
                disabled={submitting || !selectedReviewer || !selectedReviewee}
              >
                {submitting ? 'Adding...' : 'Add Nomination'}
              </Button>
              <Button
                varient="secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setSelectedReviewer(null);
                  setSelectedReviewee(null);
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          </Card>
        )}

        <div>
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4 flex items-center gap-2">
            <MdPeople className="h-5 w-5" />
            Existing Nominations ({nominations.length})
          </h3>
          
          {nominations.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-navy-800 rounded-lg">
              <MdPeople className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No peer nominations yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {nominations.map((nomination) => (
                <Card key={nomination.id} extra="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reviewer</p>
                          <p className="font-semibold text-navy-700 dark:text-white">
                            {nomination.reviewer_name || nomination.reviewer?.full_name || 'Unknown'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reviewee</p>
                          <p className="font-semibold text-navy-700 dark:text-white">
                            {nomination.reviewee_name || nomination.reviewee?.full_name || 'Unknown'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Nominated By</p>
                          <p className="text-sm text-navy-700 dark:text-white">
                            {nomination.nominated_by_name || nomination.nominated_by?.full_name || 'Unknown'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button
                      varient="secondary"
                      onClick={() => handleDeleteNomination(nomination.id)}
                      disabled={submitting}
                      className="flex items-center gap-2 text-red-600 hover:text-red-700"
                    >
                      <MdDelete className="h-5 w-5" />
                      Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PeerNominations;
